import chroma from 'chroma-js';

export type ColorScale = {
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
  950: string;
};

const STEPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const;

const LUMINANCE_TARGETS: Record<(typeof STEPS)[number], number> = {
  50: 0.92,
  100: 0.81,
  200: 0.66,
  300: 0.48,
  400: 0.3,
  500: 0.18,
  600: 0.13,
  700: 0.1,
  800: 0.07,
  900: 0.05,
  950: 0.02,
};

const scaleCache = new Map<string, ColorScale>();

/**
 * Generates an 11-step color scale from a single hex color.
 * Preserves hue, adjusts luminance to hit each target step.
 * Results are cached by seed hex to avoid redundant computation.
 */
export const generateColorScale = (seedHex: string): ColorScale => {
  const cached = scaleCache.get(seedHex);
  if (cached) return cached;

  const seed = chroma(seedHex);

  const result = {} as ColorScale;
  for (const step of STEPS) {
    result[step] = seed.luminance(LUMINANCE_TARGETS[step]).hex();
  }

  scaleCache.set(seedHex, result);
  return result;
};

/**
 * WCAG AA contrast: returns '#FFFFFF' or '#000000' depending on which
 * provides >= 4.5:1 contrast against the given background.
 */
export const getContrastText = (background: string): string => {
  return chroma.contrast(background, '#FFFFFF') >= 4.5 ? '#FFFFFF' : '#000000';
};
